import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { WorkProduct, Feature, Initiative } from "../types";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { clearEditorSurface, setEditorSurface } from "@/state/activeEditor/activeEditorSlice";
import { AccessProvider, CAPABILITIES, RequirePermission, useAccess } from "@/security/access";
import { CollabSessionProvider } from "@/collab/CollabSessionContext";
import { getHostDocumentBridge } from "@/components/aiBridge";
import { documentSessionManager } from "@/components/aiBridge/documentSessionManagerSingleton";
import { resolveCollabRoom } from "@/collab/roomResolver";
import type { RoomParts } from "@/collab/roomName";
import WorkProductEditor_Text from "./WorkProductEditor_Text";
import WorkProductEditor_Code from "./WorkProductEditor_Code";
import { resolveWorkProductEditorSurface } from "../utils/workProductEditorSurface";
import { resolveWorkProductEditorScope } from "../utils/workProductEditorScope";

interface WorkProductEditorProps {
    workProduct: WorkProduct | null;
    onBack: () => void;
    onJiraSyncOpen?: (context: "workproduct") => void;
    onAIChatToggle?: () => void;
    showAIChat?: boolean;
    selectedProject?: Initiative | null;
    selectedFeature?: Feature | null;
    onRebuildStart?: (workProductId: string, projectId?: number, featureId?: string | number, userStoryId?: string) => void;
    onProgressUpdate?: (workProductId: string, projectId?: number, featureId?: string | number, userStoryId?: string, update?: { progress?: number; message?: string }) => void;
    onRebuildJobComplete?: (workProductId: string, projectId?: number, featureId?: string | number, userStoryId?: string) => void;
    onNavigateToIntegrations?: () => void;
}

const loadingFallback = (
    <Box role="status" aria-label="Checking Work Product access" sx={{ height: "100vh", display: "grid", placeItems: "center" }}>
        <CircularProgress size={28} />
    </Box>
);

const deniedFallback = (
    <Box role="alert" sx={{ height: "100vh", display: "grid", placeItems: "center", p: 3 }}>
        <Typography>You do not have access to this Work Product.</Typography>
    </Box>
);

const WorkProductEditorAccessBoundary: React.FC<WorkProductEditorProps> = props => {
    const access = useAccess();
    const token = useAppSelector(state => state.clientToken?.token ?? "");
    const canWrite = access.hasPermission(CAPABILITIES.PRODUCT_WRITE);
    const canGenerate = access.hasPermission(CAPABILITIES.GENERATION_EXECUTE);
    const previousWritable = useRef<boolean | null>(null);
    const [collabGeneration, setCollabGeneration] = useState(0);
    const [codeRoomParts, setCodeRoomParts] = useState<RoomParts | null>(null);
    const editorSurface = resolveWorkProductEditorSurface(props.workProduct);
    const isCode = editorSurface === "ide";
    const productId = Number(access.productId);
    const workProductId = Number(props.workProduct?.id);
    const { featureId, userStoryId } = resolveWorkProductEditorScope(props.workProduct, props.selectedFeature);

    useEffect(() => {
        const controller = new AbortController();
        setCodeRoomParts(null);
        if (
            !isCode || access.status !== "ready" || !access.hasPermission(CAPABILITIES.PRODUCT_READ) ||
            !token || !Number.isSafeInteger(productId) || productId <= 0 ||
            !Number.isSafeInteger(workProductId) || workProductId <= 0
        ) return () => controller.abort();
        void resolveCollabRoom({ productId, workProductId, featureId, userStoryId }, token, controller.signal)
            .then(room => { if (!controller.signal.aborted) setCodeRoomParts(room); })
            .catch(() => { if (!controller.signal.aborted) setCodeRoomParts(null); });
        return () => controller.abort();
    }, [access.policyEpoch, access.requestId, access.status, featureId, isCode, productId, token, userStoryId, workProductId]);

    useEffect(() => {
        const bridge = getHostDocumentBridge();
        bridge.adapter.setReadOnly(!canWrite);
        bridge.monacoAdapter.setReadOnly(!canWrite);
        return () => {
            bridge.adapter.setReadOnly(true);
            bridge.monacoAdapter.setReadOnly(true);
        };
    }, [canWrite]);

    useEffect(() => {
        if (access.status !== "ready") return;
        if (previousWritable.current === true && !canWrite) {
            documentSessionManager.revokeAll();
            setCollabGeneration(value => value + 1);
        }
        previousWritable.current = canWrite;
    }, [access.status, canWrite]);

    const editor = isCode
        ? <WorkProductEditor_Code {...props} AIChatToggled={props.showAIChat} readOnly={!canWrite} />
        : <WorkProductEditor_Text {...props} AIChatToggled={props.showAIChat} readOnly={!canWrite} canGenerate={canGenerate} />;

    return (
        <RequirePermission
            required={CAPABILITIES.PRODUCT_READ}
            loadingFallback={loadingFallback}
            fallback={deniedFallback}
        >
            <CollabSessionProvider
                key={`${access.policyEpoch ?? "none"}:${canWrite}:${collabGeneration}`}
                roomPartsOverride={isCode ? codeRoomParts : undefined}
            >
                {editor}
            </CollabSessionProvider>
        </RequirePermission>
    );
};

const WorkProductEditor: React.FC<WorkProductEditorProps> = props => {
    const dispatch = useAppDispatch();
    const resolvedSurface = resolveWorkProductEditorSurface(props.workProduct);
    const productId = useMemo(() => {
        const value = Number(props.selectedProject?.id);
        return Number.isSafeInteger(value) && value > 0 ? value : null;
    }, [props.selectedProject?.id]);

    useEffect(() => {
        if (resolvedSurface) dispatch(setEditorSurface(resolvedSurface));
        else dispatch(clearEditorSurface());
    }, [dispatch, resolvedSurface]);

    useEffect(() => () => { dispatch(clearEditorSurface()); }, [dispatch]);

    if (!props.workProduct) return null;

    return (
        <AccessProvider
            productId={productId}
            surface="editor.mutations"
            workspaceBinding={productId ? `web-editor:${productId}` : "web-editor:none"}
        >
            <WorkProductEditorAccessBoundary {...props} />
        </AccessProvider>
    );
};

export default WorkProductEditor;
